import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Users, Trophy, MapPin, ArrowUpRight, Zap, Snowflake, Flag } from "lucide-react";
import AnimatedBackground from "../components/AnimatedBackground";
import { TOURNAMENT_INFO, TEAMS, SCHEDULE, ASSETS } from "../data/tournament";

const stats = [
  { label: "Équipes", value: TEAMS.length, icon: Users },
  { label: "Parties", value: SCHEDULE.length, icon: Zap },
  { label: "Catégories", value: 2, icon: Flag },
  { label: "Glaces", value: 3, icon: MapPin },
];

const StatCard = ({ icon: Icon, label, value, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: i * 0.08, duration: 0.6 }}
    className="relative bg-[#111] border border-white/10 p-6 hover:border-[#E60012]/60 transition-all hover:-translate-y-1 clip-jersey overflow-hidden group"
    data-testid={`stat-${label.toLowerCase()}`}
  >
    <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#E60012]/10 rounded-full blur-2xl group-hover:bg-[#E60012]/30 transition" />
    <Icon className="w-6 h-6 text-[#E60012] mb-4" strokeWidth={2} />
    <div className="font-display text-6xl text-white leading-none">{value}</div>
    <div className="mt-2 font-condensed uppercase tracking-[0.25em] text-xs text-white/60">
      {label}
    </div>
  </motion.div>
);

const Accueil = () => {
  const featuredFinals = SCHEDULE.filter((g) => g.phase?.startsWith("Finale D2"));

  return (
    <div className="bg-[#0a0a0a] text-white" data-testid="accueil-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <AnimatedBackground image={ASSETS.kids_red} intensity="medium" />
        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#E60012]" />
              <span className="font-condensed uppercase tracking-[0.4em] text-xs text-white/70">
                Bienvenue · Édition 2026
              </span>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
              La <span className="text-[#E60012]">passion</span> sur glace
              <br />
              commence ici.
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-2xl leading-relaxed">
              Trois jours d'intensité, plusieurs équipes de la relève, trois glaces sous un même toit.
              La Coupe CH réunit le hockey M9 — catégories 2017 et 2018 — au CCSSJ de Sainte-Julie.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/horaire"
                data-testid="hero-cta-horaire"
                className="inline-flex items-center gap-3 px-7 py-3.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.2em] text-sm transition-all hover:-translate-y-0.5 clip-tab"
              >
                <Calendar className="w-4 h-4" /> Horaire complet
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 flex-col items-center gap-2 bg-black/60 backdrop-blur-md border border-[#E60012]/40 px-8 py-6 clip-jersey"
          >
            <Snowflake className="w-6 h-6 text-[#E60012]" />
            <div className="font-display text-7xl leading-none">1·2·3</div>
            <div className="font-condensed uppercase tracking-[0.4em] text-xs text-white/70">
              Mai 2026
            </div>
          </motion.div>
        </div>
      </section>

      {/* À PROPOS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 sm:py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
              À propos
            </span>
            <h2 className="mt-3 font-display text-5xl lg:text-6xl uppercase leading-[0.95]">
              Plus qu'un tournoi.
              <br />
              <span className="text-[#E60012]">Une expérience.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              La Coupe CH a été créée pour offrir aux jeunes joueurs une compétition de calibre, dans
              un environnement festif et bienveillant. De l'ouverture du vendredi à la grande finale
              du dimanche, chaque partie compte.
            </p>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[4/3] overflow-hidden clip-jersey border border-white/10">
              <img
                src={ASSETS.team_ice}
                alt="Équipe sur la glace"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="font-condensed uppercase tracking-[0.3em] text-xs text-white/70">
                    L'enjeu
                  </div>
                  <div className="font-display text-3xl">La Coupe</div>
                </div>
                <Trophy className="w-10 h-10 text-[#E60012]" />
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#E60012] px-6 py-3 clip-tab">
              <span className="font-condensed uppercase tracking-[0.25em] text-sm font-semibold">
                Édition 2026
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative py-12 border-y border-white/10 bg-[#070707] overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap gap-12 font-display text-7xl uppercase tracking-tight">
          {["a", "b"].map((k) => (
            <div key={k} className="flex items-center gap-12 shrink-0">
              <span className="text-white">Coupe CH</span>
              <span className="text-[#E60012]">★</span>
              <span className="text-white/30">1·2·3 Mai 2026</span>
              <span className="text-[#E60012]">★</span>
              <span className="text-white">Une Passion</span>
              <span className="text-[#E60012]">★</span>
              <span className="text-white/30">M9 · 2017 / 2018</span>
              <span className="text-[#E60012]">★</span>
              <span className="text-white">CCSSJ · Sainte-Julie</span>
              <span className="text-[#E60012]">★</span>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
              Aperçu
            </span>
            <h2 className="mt-2 font-display text-5xl lg:text-6xl uppercase leading-none">
              Place au <span className="text-[#E60012]">jeu</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[ASSETS.dressing_room, ASSETS.bench, ASSETS.kids_red].map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative aspect-[4/3] overflow-hidden border border-white/10 clip-jersey group"
              data-testid={`gallery-${i}`}
            >
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-60 group-hover:opacity-30 transition" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED FINALS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
              Dimanche 3 mai
            </span>
            <h2 className="mt-2 font-display text-5xl lg:text-6xl uppercase leading-none">
              Les <span className="text-[#E60012]">finales</span>
            </h2>
          </div>
          <Link
            to="/horaire"
            data-testid="finals-link"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#E60012] font-condensed uppercase tracking-[0.2em] text-sm transition-colors"
          >
            Tout l'horaire <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {featuredFinals.map((g, i) => (
            <motion.div
              key={g.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative p-8 bg-gradient-to-br from-[#1a0306] to-[#0a0a0a] border border-[#E60012]/30 clip-jersey overflow-hidden"
              data-testid={`final-card-${g.category}`}
            >
              <div className="absolute top-0 right-0 bg-[#E60012] px-3 py-1 font-condensed uppercase tracking-[0.2em] text-xs">
                {g.phase}
              </div>
              <div className="font-condensed uppercase tracking-[0.3em] text-xs text-white/60">
                Catégorie {g.category}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50">
                    Visiteur
                  </div>
                  <div className="font-display text-3xl mt-1">{g.away}</div>
                </div>
                <div className="text-[#E60012] font-display text-4xl">VS</div>
                <div className="text-right">
                  <div className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50">
                    Local
                  </div>
                  <div className="font-display text-3xl mt-1">{g.home}</div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-6 text-sm text-white/70">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#E60012]" /> {g.time}
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#E60012]" /> {g.ice}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative py-20 overflow-hidden mt-12">
        <div className="absolute inset-0 diagonal-stripes opacity-50" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          <h3 className="font-display text-4xl lg:text-5xl uppercase leading-tight">
            Prêt à vivre la <span className="text-[#E60012]">Coupe CH</span> ?
          </h3>
          <Link
            to="/horaire"
            data-testid="bottom-cta"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#E60012] hover:bg-white hover:text-black text-white font-condensed uppercase tracking-[0.2em] text-sm transition-all hover:-translate-y-0.5 clip-tab"
          >
            Consulter l'horaire <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Accueil;
